import { api } from './api';
import { Document, DocumentVersion, DocumentPermission, PaginatedResponse } from '../types';

// Base URLs from environment variables
const API_URL = process.env.REACT_APP_API_URL || '';

interface UploadResponse {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: 'processing' | 'completed' | 'failed';
  uploadedAt: string;
  message?: string;
  processingDetails?: {
    extractedText?: string;
    pageCount?: number;
    processingTime?: number;
  };
}

interface DocumentMetadata {
  title: string;
  description?: string;
  category: string;
  tags: string[];
  permissions?: {
    canRead: string[];
    canWrite: string[];
    canDelete: string[];
  };
}

class DocumentService {
  /**
   * Get access token from sessionStorage
   */
  private getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Get headers with Authorization token
   * Cookie認証廃止により、Authorization headerのみを使用
   */
  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Upload document
  async uploadDocument(
    file: File,
    metadata: DocumentMetadata,
    onProgress?: (progress: number) => void,
    options?: { asNewVersion?: boolean }
  ): Promise<UploadResponse> {
    try {
      // Create FormData for the upload endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', metadata.category || 'general');
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      if (metadata.tags && metadata.tags.length > 0) {
        formData.append('tags', metadata.tags.join(','));
      }
      // Add as_new_version flag if specified (for handling duplicate uploads)
      if (options?.asNewVersion) {
        formData.append('as_new_version', 'true');
      }

      // Import axios directly since we need special handling for file upload
      const axios = (await import('axios')).default;

      // Upload to the documents endpoint (saves to database)
      // Note: Do NOT set Content-Type manually for FormData - axios will set it
      // automatically with the correct boundary parameter
      const token = this.getAccessToken();
      const response = await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: {
          // Let axios set Content-Type automatically for multipart/form-data
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
        timeout: 120000,  // 2 minutes timeout for large file uploads
        onUploadProgress: (progressEvent: any) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return {
        id: response.data.id,
        fileName: response.data.fileName,
        fileSize: response.data.fileSize,
        mimeType: response.data.mimeType,
        status: response.data.status || 'completed',
        uploadedAt: response.data.uploadedAt,
        message: response.data.message || 'Upload successful',
      };
    } catch (error: any) {
      console.error('Upload failed:', error);

      // Handle 409 Conflict (duplicate file) specially
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;

        // Handle both object and string formats
        if (detail && typeof detail === 'object') {
          // Object format: { message: string, existingDocument: {...} }
          const duplicateError: any = new Error(detail.message || '同じファイルが既にアップロードされています');
          duplicateError.isDuplicate = true;
          duplicateError.existingDocument = detail.existingDocument;
          throw duplicateError;
        } else if (typeof detail === 'string') {
          // String format: "同じファイルが既にアップロードされています"
          const duplicateError: any = new Error(detail);
          duplicateError.isDuplicate = true;
          duplicateError.existingDocument = null;
          throw duplicateError;
        }
      }

      // Extract error message from FastAPI response
      const errorMessage = error.response?.data?.detail?.message
        || error.response?.data?.detail
        || error.response?.data?.message
        || error.message
        || 'Upload failed';

      throw new Error(errorMessage);
    }
  }

  // Get documents list
  async getDocuments(params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Document>> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.pageSize) queryParams.append('page_size', params.pageSize.toString());
      if (params?.category) queryParams.append('category', params.category);
      if (params?.tags?.length) queryParams.append('tags', params.tags.join(','));

      // Map frontend sort fields to backend field names
      if (params?.sortBy) {
        const sortFieldMap: { [key: string]: string } = {
          'date': 'uploadedAt',
          'name': 'fileName',
          'size': 'fileSize',
          'category': 'category',
        };
        const backendSortField = sortFieldMap[params.sortBy] || params.sortBy;
        queryParams.append('sort_by', backendSortField);
      }
      if (params?.sortOrder) queryParams.append('sort_order', params.sortOrder);

      const response = await fetch(`${API_URL}/documents/?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      return await response.json();
    } catch (error) {
      console.warn('Document list API not available, returning empty results');
      // Return empty results for now - backend endpoint not implemented yet
      return {
        results: [],
        total: 0,
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        totalPages: 0,
      };
    }
  }

  // Get document by ID
  async getDocument(id: string): Promise<Document> {
    try {
      const response = await fetch(`${API_URL}/documents/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      return await response.json();
    } catch (error) {
      console.warn('Document detail API not available');
      throw new Error('Document detail API not yet implemented');
    }
  }

  // Update document metadata
  async updateDocument(id: string, updates: Partial<DocumentMetadata>): Promise<Document> {
    return api.patch(`/documents/${id}`, updates);
  }

  // Update document tags
  async updateDocumentTags(id: string, tags: string[]): Promise<{ message: string; tags: string[] }> {
    return api.patch(`/documents/${id}/tags`, { tags });
  }

  // Delete document
  async deleteDocument(id: string): Promise<void> {
    return api.delete(`/documents/${id}`);
  }

  // Get document versions
  async getDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
    try {
      return await api.get(`/documents/${documentId}/versions`);
    } catch (error) {
      console.warn('Document versions API not available');
      return []; // Return empty array for now
    }
  }

  // Upload new version
  async uploadNewVersion(
    documentId: string,
    file: File,
    changeLog?: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentVersion> {
    const formData = new FormData();
    formData.append('file', file);
    if (changeLog) formData.append('changeLog', changeLog);

    // Import axios directly since we need special handling for file upload
    const axios = (await import('axios')).default;

    // Note: Do NOT set Content-Type manually for FormData - axios will set it
    // automatically with the correct boundary parameter
    const token = this.getAccessToken();
    const response = await axios.post(`${API_URL}/documents/${documentId}/versions`, formData, {
      headers: {
        // Let axios set Content-Type automatically for multipart/form-data
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
      timeout: 120000,  // 2 minutes timeout for large file uploads
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Restore previous version
  async restoreVersion(documentId: string, versionId: string): Promise<Document> {
    return api.post(`/documents/${documentId}/restore/${versionId}`);
  }

  // Download document
  async downloadDocument(documentId: string, versionId?: string): Promise<Blob> {
    const url = versionId
      ? `/documents/${documentId}/download?version=${versionId}`
      : `/documents/${documentId}/download`;

    const response = await fetch(`${API_URL}${url}`, {
      method: 'GET',
      headers: this.getHeaders(false),
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    return response.blob();
  }

  // Get document extracted text content
  async getDocumentContent(documentId: string): Promise<{
    document_id: string;
    content: string;
    file_name: string;
    title: string;
  }> {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}/content`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to fetch document content');
      }

      return await response.json();
    } catch (error) {
      console.error('Document content fetch failed:', error);
      throw error;
    }
  }

  // Get document permissions
  async getDocumentPermissions(documentId: string): Promise<DocumentPermission[]> {
    try {
      return await api.get(`/documents/${documentId}/permissions`);
    } catch (error) {
      console.warn('Document permissions API not available');
      return []; // Return empty array for now
    }
  }

  // Update document permissions
  async updateDocumentPermissions(
    documentId: string,
    permissions: {
      add?: DocumentPermission[];
      remove?: string[]; // permission IDs to remove
    }
  ): Promise<DocumentPermission[]> {
    return api.post(`/documents/${documentId}/permissions`, permissions);
  }

  // Share document
  async shareDocument(
    documentId: string,
    shareOptions: {
      users?: string[];
      groups?: string[];
      permissions: ('read' | 'write' | 'delete')[];
      expiresAt?: string;
    }
  ): Promise<{ shareLink?: string; permissions: DocumentPermission[] }> {
    return api.post(`/documents/${documentId}/share`, shareOptions);
  }

  // Get upload status
  async getUploadStatus(uploadId: string): Promise<UploadResponse> {
    try {
      // Try to get actual status from backend
      const response = await fetch(`${API_URL}/documents/${uploadId}/status`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (response.ok) {
        return await response.json();
      }

      // If endpoint doesn't exist (404), fall back to checking document exists
      if (response.status === 404) {
        // Try to get document info directly
        const docResponse = await fetch(`${API_URL}/documents/${uploadId}`, {
          method: 'GET',
          headers: this.getHeaders(),
        });

        if (docResponse.ok) {
          const doc = await docResponse.json();
          return {
            id: uploadId,
            fileName: doc.fileName || doc.title,
            fileSize: doc.fileSize || 0,
            mimeType: doc.mimeType || 'application/octet-stream',
            status: 'completed',
            uploadedAt: doc.uploadedAt || doc.createdAt || new Date().toISOString(),
            message: 'Processing completed',
          };
        }
      }

      throw new Error('Failed to get upload status');
    } catch (error) {
      console.warn('Upload status API not available, returning pending status');
      // Return pending status if we can't verify
      return {
        id: uploadId,
        fileName: 'Unknown',
        fileSize: 0,
        mimeType: 'application/octet-stream',
        status: 'processing',
        uploadedAt: new Date().toISOString(),
        message: 'Unable to retrieve status',
      };
    }
  }

  // Process document with Azure Document Intelligence
  async processDocument(documentId: string): Promise<{
    extractedText: string;
    layout: any;
    tables: any[];
    keyValuePairs: any[];
  }> {
    return api.post(`/documents/${documentId}/process`);
  }

  // Generate embeddings for document
  async generateEmbeddings(documentId: string): Promise<{
    embeddings: number[][];
    chunks: string[];
  }> {
    return api.post(`/documents/${documentId}/embeddings`);
  }

  // Index document in Azure AI Search
  async indexDocument(documentId: string): Promise<{
    indexed: boolean;
    indexName: string;
    documentCount: number;
  }> {
    return api.post(`/documents/${documentId}/index`);
  }

  // Get document images
  async getDocumentImages(documentId: string): Promise<{
    documentId: string;
    totalImages: number;
    images: Array<{
      id: string;
      pageNumber: number;
      imageIndex: number;
      imageType: string;
      fileFormat: string;
      fileSize: number;
      width: number;
      height: number;
      storagePath: string;
      storageUrl: string | null;
      extractedText: string | null;
      aiDescription: string | null;
      createdAt: string;
    }>;
  }> {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}/images`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch document images');
      }

      return await response.json();
    } catch (error) {
      console.warn('Document images API not available');
      return {
        documentId,
        totalImages: 0,
        images: [],
      };
    }
  }

  // Get document image URL (for reference only - use getDocumentImageBlob for display)
  getDocumentImageUrl(documentId: string, imageId: string): string {
    return `${API_URL}/documents/${documentId}/images/${imageId}`;
  }

  // Fetch document image with authentication and return blob URL
  async getDocumentImageBlob(documentId: string, imageId: string): Promise<string> {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}/images/${imageId}`, {
        method: 'GET',
        headers: this.getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to fetch image blob:', error);
      throw error;
    }
  }

  // Analyze single image with AI
  async analyzeImage(documentId: string, imageId: string): Promise<{
    imageId: string;
    aiDescription: string;
    context: string;
    message: string;
  }> {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}/images/${imageId}/analyze`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze image');
      }

      return await response.json();
    } catch (error) {
      console.error('Image analysis failed:', error);
      throw error;
    }
  }

  // Analyze all images in document
  async analyzeAllImages(documentId: string, forceReanalyze: boolean = false): Promise<{
    documentId: string;
    totalImages: number;
    analyzedImages: number;
    context: string;
    results: Array<{
      imageId: string;
      pageNumber: number;
      imageIndex: number;
      status: string;
      aiDescription?: string;
      error?: string;
    }>;
    message: string;
  }> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/analyze-all`);
      if (forceReanalyze) {
        url.searchParams.append('force_reanalyze', 'true');
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze images');
      }

      return await response.json();
    } catch (error) {
      console.error('Batch image analysis failed:', error);
      throw error;
    }
  }

  // Re-extract images from document
  async reExtractImages(
    documentId: string,
    options: {
      asBlueprint?: boolean;
      dpi?: number;
      runAiAnalysis?: boolean;
    } = {}
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      document_id: string;
      extraction_mode: string;
      images_extracted: number;
      images_saved: number;
      images_analyzed: number;
      ai_analysis_enabled: boolean;
    };
  }> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/re-extract-images`);

      if (options.asBlueprint !== undefined) {
        url.searchParams.append('as_blueprint', String(options.asBlueprint));
      }
      if (options.dpi !== undefined) {
        url.searchParams.append('dpi', String(options.dpi));
      }
      if (options.runAiAnalysis !== undefined) {
        url.searchParams.append('run_ai_analysis', String(options.runAiAnalysis));
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to re-extract images');
      }

      return await response.json();
    } catch (error) {
      console.error('Image re-extraction failed:', error);
      throw error;
    }
  }

  // Compare two images
  async compareImages(
    documentId: string,
    image1Id: string,
    image2Id: string,
    comparisonType: 'changes' | 'evolution' | 'differences' = 'changes'
  ): Promise<{
    documentId: string;
    image1: { id: string; pageNumber: number; imageIndex: number };
    image2: { id: string; pageNumber: number; imageIndex: number };
    comparisonType: string;
    analysis: string;
    message: string;
  }> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/compare`);
      url.searchParams.append('image1_id', image1Id);
      url.searchParams.append('image2_id', image2Id);
      url.searchParams.append('comparison_type', comparisonType);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to compare images');
      }

      return await response.json();
    } catch (error) {
      console.error('Image comparison failed:', error);
      throw error;
    }
  }

  // Analyze a sequence of images
  async analyzeImageSequence(
    documentId: string,
    imageIds: string[]
  ): Promise<{
    documentId: string;
    imageCount: number;
    imageIds: string[];
    analysis: string;
    message: string;
  }> {
    try {
      const url = new URL(`${API_URL}/documents/${documentId}/images/analyze-sequence`);

      // Add each image ID as a separate query parameter
      imageIds.forEach(id => {
        url.searchParams.append('image_ids', id);
      });

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to analyze image sequence');
      }

      return await response.json();
    } catch (error) {
      console.error('Image sequence analysis failed:', error);
      throw error;
    }
  }

  // Re-extract metadata (title, summary, tags) using LLM
  async reExtractMetadata(
    documentId: string,
    options: {
      extractTitle?: boolean;
      extractSummary?: boolean;
      extractTags?: boolean;
    } = {}
  ): Promise<{
    document_id: string;
    title?: string;
    summary?: string;
    tags?: string[];
    updated_fields: string[];
    message: string;
  }> {
    try {
      // Use FormData to match backend Form() parameters
      const formData = new FormData();
      formData.append('extract_title', String(options.extractTitle ?? true));
      formData.append('extract_summary', String(options.extractSummary ?? true));
      formData.append('extract_tags', String(options.extractTags ?? true));

      const token = this.getAccessToken();
      const response = await fetch(`${API_URL}/documents/${documentId}/re-extract-metadata`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to re-extract metadata');
      }

      return await response.json();
    } catch (error) {
      console.error('Metadata re-extraction failed:', error);
      throw error;
    }
  }
}

export const documentService = new DocumentService();
export default documentService;
